import { track } from "@/utils/events/tracker";
import { create } from "zustand";
import {
  AnyChangeLedgerItem,
  ChangeType,
  RedoChange,
  UndoChange,
} from "./changeLedgerTypes";
import useActivityStore from "./activityStore";

// These constants match what tempo-web and tempo-devtools has
export const WRAP_IN_DIV_PLACEHOLDER_CODEBASE_ID =
  "tempo-wrap-in-div-placeholder";
export const DUPLICATE_PLACEHOLDER_PREFIX = "tempo-duplicate-placeholder-";

export interface ChangeLedgerState {
  ledger: AnyChangeLedgerItem[];
  ledgerCanvasId: string;
  flushing: boolean;
  ongoingChange: boolean; // Whether we are currently in a change in progress (e.g. dragging and dropping in canvas)
  triggerFlush: boolean; // If set to true -> triggers flush
  setLedger: (ledger: AnyChangeLedgerItem[], canvasId: string) => void;
  clearLedger: (canvasId: string) => void;
  addChange: (changeFields: AnyChangeLedgerItem) => void;
  addUndoChange: () => UndoChange | null;
  addRedoChange: () => RedoChange | null;
  updateChange: (changeItem: AnyChangeLedgerItem) => void;
  setFlushing: (flushing: boolean) => void;
  setTriggerFlush: (trigger: boolean) => void;
  setOngoingChange: (ongoingChange: boolean) => void;

  changeBeingProcessed: boolean;
  setChangeBeingProcessed: (changeBeingProcessed: boolean) => void;
}

const trackLedgerItem = (item: AnyChangeLedgerItem) => {
  track(item.type);
};

export const resolveCurrentLedgerChain = (
  ledger: AnyChangeLedgerItem[]
): {
  resolvedLedgerChain: AnyChangeLedgerItem[];
  currentIndexInResolvedChain: number;
} => {
  let resolvedLedgerChain = [];
  let currentIndexInResolvedChain = -1;

  ledger.forEach((item) => {
    // Skip any that have failed
    if (item.isFailed()) {
      return;
    }

    if (item.type === ChangeType.UNDO) {
      if (currentIndexInResolvedChain >= 0) {
        currentIndexInResolvedChain -= 1;
      }
    } else if (item.type === ChangeType.REDO) {
      if (currentIndexInResolvedChain < resolvedLedgerChain.length - 1) {
        currentIndexInResolvedChain += 1;
      }
    } else {
      // Regular change, any changes that were undone in this moment are erased from the ledger
      resolvedLedgerChain = [
        // @ts-expect-error TS2322
        ...resolvedLedgerChain.slice(0, currentIndexInResolvedChain + 1),
        // @ts-expect-error TS2322
        item,
      ];
      currentIndexInResolvedChain += 1;
    }
  });

  return { resolvedLedgerChain, currentIndexInResolvedChain };
};

export const useChangeLedgerStore = create<ChangeLedgerState>((set, get) => ({
  ledger: [],
  // @ts-expect-error TS2322
  ledgerCanvasId: null,
  flushing: false,
  ongoingChange: false,
  triggerFlush: false,
  clearLedger: (canvasId: string) =>
    set({ ledger: [], ledgerCanvasId: canvasId }),
  setLedger: (newLedger: AnyChangeLedgerItem[], canvasId: string): void => {
    newLedger.forEach((item, index) => (item.indexInLedger = index));
    set({ ledger: newLedger, ledgerCanvasId: canvasId });
  },
  addChange: (newLedgerItem: AnyChangeLedgerItem): void => {
    const state = get();

    newLedgerItem.indexInLedger = state.ledger.length;

    trackLedgerItem(newLedgerItem);
    set({ ledger: [...state.ledger, newLedgerItem] });
  },
  addUndoChange: (): UndoChange | null => {
    // If restoring, do not apply at the same time
    const restoringActivityId = useActivityStore.getState().restoringActivityId;
    if (restoringActivityId) {
      return null;
    }

    const state = get();

    const { resolvedLedgerChain, currentIndexInResolvedChain } =
      resolveCurrentLedgerChain(state.ledger);

    if (currentIndexInResolvedChain >= 0) {
      const changeToUndo = resolvedLedgerChain[currentIndexInResolvedChain];
      const newChange = new UndoChange({
        changeToUndo,
      });
      changeToUndo.changeUndone = true;
      state.addChange(newChange);
      return newChange;
    }

    // No change to undo
    return null;
  },
  addRedoChange: (): RedoChange | null => {
    // If restoring, do not apply at the same time
    const restoringActivityId = useActivityStore.getState().restoringActivityId;
    if (restoringActivityId) {
      return null;
    }

    const state = get();

    const { resolvedLedgerChain, currentIndexInResolvedChain } =
      resolveCurrentLedgerChain(state.ledger);

    if (currentIndexInResolvedChain < resolvedLedgerChain.length - 1) {
      const changeToRedo = resolvedLedgerChain[currentIndexInResolvedChain + 1];
      const newChange = new RedoChange({
        changeToRedo,
      });
      changeToRedo.changeUndone = false;
      state.addChange(newChange);
      return newChange;
    }

    // No change to redo
    return null;
  },
  updateChange: (changeItem) => {
    const state = get();
    if (
      // @ts-expect-error TS2538
      !state.ledger[changeItem.indexInLedger] ||
      // @ts-expect-error TS2538
      state.ledger[changeItem.indexInLedger].id !== changeItem.id
    ) {
      console.error(
        "Tried to update a change that does not exist in the ledger"
      );
      return;
    }

    // @ts-expect-error TS2538
    state.ledger[changeItem.indexInLedger] = changeItem;
    set({ ledger: [...state.ledger] });
  },
  setFlushing: (flushing) => set({ flushing }),
  setTriggerFlush: (trigger) => set({ triggerFlush: trigger }),
  setOngoingChange: (ongoingChange) => set({ ongoingChange }),

  changeBeingProcessed: false,
  setChangeBeingProcessed: (changeBeingProcessed: boolean) =>
    set({ changeBeingProcessed }),
}));
